import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import TodayOutlinedIcon from "@mui/icons-material/TodayOutlined";
import ViewTimelineOutlinedIcon from "@mui/icons-material/ViewTimelineOutlined";
import { Alert, Box, Button, CircularProgress, Stack, Tab, Tabs, Typography } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { fetchOssOrders } from "../../api/ossApi";
import { useOssAsyncData } from "../../hooks/useOssAsyncData";
import { OssPanel } from "../common/OssPanel";
import { OssDashboardConnectedCalendar } from "./OssDashboardConnectedCalendar";
import { OssDashboardGanttView } from "./OssDashboardGanttView";
import { OssDashboardWorkFocus } from "./OssDashboardWorkFocus";
import { normalizeTimelineRows } from "../../utils/timelineUtils";

const viewOptions = [
  { key: "today", label: "업무 요약", icon: <TodayOutlinedIcon fontSize="small" /> },
  { key: "calendar", label: "일정 보기", icon: <CalendarMonthOutlinedIcon fontSize="small" /> },
  { key: "gantt", label: "주문 생애주기", icon: <ViewTimelineOutlinedIcon fontSize="small" /> },
];

export const OssDashboardTimelinePanel = ({ onOpenOrder, lifecycleOrders, businessDate, stageFilter = "total", ganttFocus, lifecycleLoading, refreshKey = 0 }) => {
  const { group_code, project_code } = useParams();
  const [view, setView] = useState("today");
  const previousFocus = useRef({ stageFilter, ganttFocus });
  useEffect(() => {
    if (previousFocus.current.stageFilter !== stageFilter || previousFocus.current.ganttFocus !== ganttFocus) {
      setView("gantt");
    }
    previousFocus.current = { stageFilter, ganttFocus };
  }, [stageFilter, ganttFocus]);
  const { data, loading, error, load } = useOssAsyncData({
    fetcher: () =>
      fetchOssOrders(group_code, project_code, {
        page: 1,
        pageSize: 100,
        sortBy: "requiredStartDate",
        sortDir: "ASC",
      }),
    fallbackData: { items: [], pagination: { total: 0 } },
    errorMessage: "일정 데이터를 불러오지 못했습니다.",
    deps: [group_code, project_code, refreshKey],
  });

  const rows = useMemo(
    () => normalizeTimelineRows(data?.items || []),
    [data]
  );

  return (
    <OssPanel
      className="oss-timeline-panel"
      sx={{
        flex: 1,
        minHeight: { xs: 500, lg: 0 },
        height: "100%",
        maxHeight: "100%",
        boxSizing: "border-box",
        p: 1.75,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", md: "center" }}
        spacing={1.2}
        sx={{ mb: 1.05, flexShrink: 0 }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 17, fontWeight: 900, color: "#111827" }}>
            {view === "today" ? "주요 업무 및 일정" : view === "calendar" ? "일정 보기" : "주문 생애주기"}
          </Typography>
          <Typography sx={{ mt: 0.25, fontSize: 12.5, color: "#64748b" }}>
            {view === "today"
              ? "오늘 확인할 품목과 이번 주 일정, 이번 달 납기를 모아 보여줍니다."
              : view === "calendar"
                ? "품목별 착수일부터 요청 납기까지의 일정을 보여줍니다."
                : "접수 · 확인 · 확정 · 진행 · 보류"}
          </Typography>
        </Box>
        <Tabs
          value={view}
          onChange={(_event, next) => setView(next)}
          variant="scrollable"
          scrollButtons="auto"
          className="oss-view-tabs"
          sx={{
            minHeight: 38,
            "& .MuiTabs-indicator": { display: "none" },
            "& .MuiTab-root": {
              minHeight: 38,
              minWidth: 102,
              px: 1.4,
              py: 0.7,
              mr: 0.55,
              border: "1px solid #dbe2ef",
              borderRadius: "8px",
              color: "#475569",
              fontSize: "13px !important",
              fontWeight: "800 !important",
              bgcolor: "#fff",
            },
            "& .Mui-selected": {
              color: "#fff !important",
              borderColor: "#6c5ce7",
              bgcolor: "#6c5ce7",
              boxShadow: "0 8px 18px rgba(108, 92, 231, 0.22)",
            },
          }}
        >
          {viewOptions.map((option) => (
            <Tab key={option.key} value={option.key} icon={option.icon} iconPosition="start" label={option.label} />
          ))}
        </Tabs>
      </Stack>

      {error && view !== "gantt" && (
        <Alert severity="error" action={<Button color="inherit" onClick={load}>재시도</Button>} sx={{ mb: 1, flexShrink: 0 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ flex: 1, minHeight: 0, height: "100%", maxHeight: "100%", display: "flex", overflow: "hidden" }}>
        {view !== "gantt" && loading && <Box role="status" aria-label="일정 불러오는 중" sx={{ flex: 1, display: "grid", placeItems: "center" }}><CircularProgress size={28} /></Box>}
        {view === "today" && !loading && !error && <OssDashboardWorkFocus rows={rows} onOpenOrder={onOpenOrder} />}
        {view === "calendar" && !loading && !error && (
          <OssDashboardConnectedCalendar rows={rows} onOpenOrder={onOpenOrder} />
        )}
        {view === "gantt" && (
          lifecycleOrders ? <OssDashboardGanttView orders={lifecycleOrders} businessDate={businessDate}
            stageFilter={stageFilter} loading={lifecycleLoading} onOpenOrder={onOpenOrder} />
            : <Alert severity="warning" sx={{ width: "100%", alignSelf: "flex-start" }}>주문 생애주기 데이터를 불러올 수 없습니다. 서버 업데이트 후 다시 조회해 주세요.</Alert>
        )}
      </Box>
    </OssPanel>
  );
};
